import React, { useEffect, useState, useRef } from 'react';
import { useApi } from '../hooks/useApi';

const ROUND_ORDER = ['playin', 'R64', 'R32', 'R16', 'R8', 'R4', 'Final'];
const ROUND_LABEL_DEFAULTS = {
  playin: 'First Four', R64: 'Round of 64', R32: 'Round of 32',
  R16: 'Sweet 16', R8: 'Elite Eight', R4: 'Final Four', Final: 'Championship',
};
const POLL_INTERVAL = 30000;
const INACTIVITY_LIMIT = 10 * 60 * 1000;

function cleanTeamName(name) {
  if (!name) return '';
  const words = name.trim().split(/\s+/);
  if (words.length <= 2) return name;
  const keepEndings = ['state', 'tech', 'a&m', 'dame', 'mary', 'marys', 'john', 'johns', 'joseph', 'valley', 'point', 'canyon', 'forest', 'cross'];
  if (words.length === 3 && keepEndings.includes(words[2].toLowerCase().replace(/[^a-z&]/g, ''))) return name;
  return words.slice(0, -1).join(' ');
}

export default function ScoresPage() {
  const api = useApi();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRound, setActiveRound] = useState(null);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [paused, setPaused] = useState(false);
  const [scoresEnabled, setScoresEnabled] = useState(true);
  const [playInRoundName, setPlayInRoundName] = useState('First Four');
  const [otherGameRetentionHours, setOtherGameRetentionHours] = useState(24);

  useEffect(() => {
    fetch((process.env.REACT_APP_API_URL || '/api') + '/scores/config')
      .then(r => r.json())
      .then(cfg => {
        setScoresEnabled(cfg.scoresEnabled !== false);
        setPlayInRoundName(cfg.playInRoundName || 'First Four');
        setOtherGameRetentionHours(cfg.otherGameRetentionHours ?? 24);
      })
      .catch(() => {});
  }, []);

  // Use refs for things that shouldn't trigger re-renders or effect re-runs
  const intervalRef = useRef(null);
  const inactivityRef = useRef(null);
  const pausedRef = useRef(false);
  const initializedRef = useRef(false);
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    const fetchData = async (isInitial = false) => {
      try {
        const [data, tsData] = await Promise.all([
          apiRef.current.getScores(),
          fetch((process.env.REACT_APP_API_URL || '/api') + '/scores/updated').then(r => r.json()).catch(() => ({})),
        ]);
        setGames(data);
        setError(''); // clear any previous fetch error
        const ts = tsData.lastPollAt || tsData.lastScoreUpdate || null;
        if (ts) setLastUpdated(ts);
        // Only set active round on very first load
        if (isInitial && !initializedRef.current) {
          initializedRef.current = true;
          const tourneyGames = data.filter(g => g.round !== 'other');
          const liveRound = tourneyGames.find(g => g.status === 'live')?.round;
          const latestRound = tourneyGames.filter(g => g.status === 'final').sort(
            (a, b) => ROUND_ORDER.indexOf(b.round) - ROUND_ORDER.indexOf(a.round)
          )[0]?.round;
          setActiveRound(liveRound || latestRound || 'R64');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const startPolling = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => fetchData(false), POLL_INTERVAL);
    };

    const stopPolling = () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };

    const resetInactivityTimer = () => {
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      inactivityRef.current = setTimeout(() => {
        if (!document.hidden) {
          pausedRef.current = true;
          setPaused(true);
          stopPolling();
        }
      }, INACTIVITY_LIMIT);
    };

    // Expose resume function for the "Still Here" button
    window.__scoresResume = () => {
      pausedRef.current = false;
      setPaused(false);
      fetchData(false);
      startPolling();
      resetInactivityTimer();
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
        if (inactivityRef.current) clearTimeout(inactivityRef.current);
      } else {
        // Tab visible again — just resume polling, don't force an immediate fetch
        // (the interval will fire naturally, avoiding any render disruption)
        if (!pausedRef.current) {
          startPolling();
          resetInactivityTimer();
        }
      }
    };

    // Initial load
    fetchData(true);
    startPolling();
    resetInactivityTimer();

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      stopPolling();
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
      delete window.__scoresResume;
    };
  }, []); // empty deps — runs once, uses refs for everything mutable

  const handleStillHere = () => {
    if (window.__scoresResume) window.__scoresResume();
  };

  if (!scoresEnabled) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">LIVE <span>SCORES</span></h1>
      </div>
      <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--gray)' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>🚧</div>
        <p style={{ fontFamily: 'var(--font-condensed)', fontSize: '1.1rem' }}>
          Scores are temporarily unavailable. Please check back soon.
        </p>
      </div>
    </div>
  );

  if (loading) return (
    <div className="loading-container">
      <div className="spinner"></div>
      <div className="loading-text">Loading scores...</div>
    </div>
  );

  const tournamentGames = games.filter(g => g.round !== 'other');
  const cutoff = Date.now() - otherGameRetentionHours * 60 * 60 * 1000;
  const otherGames = games.filter(g => {
    if (g.round !== 'other') return false;
    // Hide if older than retention period
    if (g.lastUpdated && new Date(g.lastUpdated).getTime() < cutoff) return false;
    return true;
  });
  const ROUND_LABELS = { ...ROUND_LABEL_DEFAULTS, playin: playInRoundName };
  const availableRounds = ROUND_ORDER.filter(r => tournamentGames.some(g => g.round === r));
  const hasOther = otherGames.length > 0 || activeRound === 'other';

  const roundGames = activeRound === 'other' ? otherGames : tournamentGames.filter(g => {
    if (g.round !== activeRound) return false;
    if (activeRound === 'Final' && g.id !== 'final-1') return false;
    if (g.homeTeam?.id === 'tbd' && g.awayTeam?.id === 'tbd') return false;
    return true;
  });

  const liveCount = tournamentGames.filter(g => g.status === 'live').length;
  const liveOtherCount = otherGames.filter(g => g.status === 'live').length;

  return (
    <div>
      {paused && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--navy-mid)', border: '1px solid var(--slate)', borderRadius: 12,
            padding: '36px 40px', textAlign: 'center', maxWidth: 360,
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>👀</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--cream)', letterSpacing: '1px', marginBottom: 8 }}>
              STILL WATCHING?
            </div>
            <p style={{ fontFamily: 'var(--font-condensed)', color: 'var(--gray)', fontSize: '0.9rem', marginBottom: 24 }}>
              Score updates have been paused after 10 minutes of inactivity.
            </p>
            <button className="btn btn-primary" onClick={handleStillHere}
              style={{ padding: '10px 28px', fontSize: '1rem', fontFamily: 'var(--font-condensed)', fontWeight: 700, letterSpacing: '1px' }}>
              Yes, Keep Me Updated
            </button>
          </div>
        </div>
      )}

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 className="page-title">LIVE <span>SCORES</span></h1>
          {liveCount > 0 && (
            <p className="page-subtitle" style={{ color: 'var(--green)' }}>
              ● {liveCount} game{liveCount !== 1 ? 's' : ''} in progress · Auto-refreshes every 30s
              {lastUpdated && <span style={{ color: 'var(--gray)', marginLeft: 8 }}>· Last updated: {new Date(lastUpdated).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
            </p>
          )}
          {liveCount === 0 && (
            <p className="page-subtitle">
              Auto-refreshes every 30 seconds
              {lastUpdated && <span style={{ color: 'var(--gray)', marginLeft: 8 }}>· Last updated: {new Date(lastUpdated).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>}
            </p>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="tabs">
        {availableRounds.map(round => {
          const liveInRound = tournamentGames.filter(g => g.round === round && g.status === 'live').length;
          return (
            <button key={round} className={`tab${activeRound === round ? ' active' : ''}`} onClick={() => setActiveRound(round)}>
              {ROUND_LABELS[round]}
              {liveInRound > 0 && <span className="status-live" style={{ marginLeft: 6, fontSize: '0.7rem' }}>●</span>}
            </button>
          );
        })}
        {hasOther && (
          <button
            className={`tab${activeRound === 'other' ? ' active' : ''}`}
            onClick={() => setActiveRound('other')}
            style={{ marginLeft: 'auto', color: activeRound === 'other' ? undefined : 'var(--gray)', borderColor: 'var(--slate)' }}
          >
            Other
            {liveOtherCount > 0 && <span className="status-live" style={{ marginLeft: 6, fontSize: '0.7rem' }}>●</span>}
          </button>
        )}
      </div>

      {roundGames.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px', color: 'var(--gray)' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>📅</div>
          <p style={{ fontFamily: 'var(--font-condensed)', fontSize: '1.1rem' }}>No games yet in this round.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {roundGames.sort((a, b) => {
            const order = { live: 0, scheduled: 1, final: 2 };
            return order[a.status] - order[b.status];
          }).map(game => (
            <GameCard key={game.id} game={game} isOther={game.round === 'other'} />
          ))}
        </div>
      )}
    </div>
  );
}

function GameCard({ game, isOther }) {
  const home = game.homeTeam;
  const away = game.awayTeam;
  const homeWon = game.winner === home?.id;
  const awayWon = game.winner === away?.id;

  const teamName = (team) => {
    if (!team) return '—';
    const name = team.name || '—';
    const seed = team.seed && team.seed !== 99 ? `(${team.seed}) ` : '';
    return `${seed}${name}`;
  };

  return (
    <div className={`score-card ${game.status}`} style={{ gap: 12, opacity: isOther ? 0.85 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)', letterSpacing: '1px', textTransform: 'uppercase' }}>
          {game.shortName}
        </span>
        <span>
          {game.status === 'live' && (
            <span className="badge badge-green">
              ● {game.isHalftime ? 'HALFTIME' : game.period ? `${game.period === 1 ? '1st' : '2nd'} ${game.displayClock || 'LIVE'}` : 'LIVE'}
            </span>
          )}
          {game.status === 'final' && <span className="badge badge-gray">FINAL</span>}
          {game.status === 'scheduled' && (
            <span className="badge badge-orange">{game.date ? new Date(game.date).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'TBD'}</span>
          )}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {away?.logo && <img src={away.logo} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />}
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1.1rem',
            color: awayWon ? 'var(--green)' : !homeWon && !awayWon ? 'var(--cream)' : 'var(--gray)' }}>
            {teamName(away)}
          </div>
        </div>
        {game.status !== 'scheduled' && (
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: awayWon ? 'var(--green)' : 'var(--cream)' }}>
            {away?.score ?? '—'}
          </div>
        )}
      </div>

      <div style={{ height: '1px', background: 'var(--slate)' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {home?.logo && <img src={home.logo} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />}
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1.1rem',
            color: homeWon ? 'var(--green)' : !homeWon && !awayWon ? 'var(--cream)' : 'var(--gray)' }}>
            {teamName(home)}
          </div>
        </div>
        {game.status !== 'scheduled' && (
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: homeWon ? 'var(--green)' : 'var(--cream)' }}>
            {home?.score ?? '—'}
          </div>
        )}
      </div>
    </div>
  );
}
